import { useEffect, useMemo, useState } from 'react';
import { App, Button, Modal, Switch, Table, Tabs } from 'antd';

import { getColumnasExcelPreferencia, guardarColumnasExcelPreferencia } from '../api';
import type { CampoCfdiDisponible } from '../api';

interface Props {
  visible: boolean;
  onClose: () => void;
}

interface FilaCampo extends CampoCfdiDisponible {
  orden: number;
}

export function ColumnasExcelModal({ visible, onClose }: Props) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [campos, setCampos] = useState<CampoCfdiDisponible[]>([]);
  const [seleccion, setSeleccion] = useState<Record<string, boolean>>({});
  const [tab, setTab] = useState<'factura' | 'pago' | 'nomina'>('factura');

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    getColumnasExcelPreferencia()
      .then(d => {
        setCampos(d.campos_disponibles);
        setSeleccion(d.seleccion);
      })
      .catch(() => message.error('No se pudo cargar la configuración de columnas.'))
      .finally(() => setLoading(false));
  }, [visible, message]);

  const filas: FilaCampo[] = useMemo(
    () =>
      campos
        .map((c, idx) => ({ ...c, orden: idx + 1 }))
        .filter(c => c.categorias.includes(tab)),
    [campos, tab],
  );

  async function handleGuardar() {
    setSaving(true);
    try {
      await guardarColumnasExcelPreferencia(seleccion);
      message.success('Columnas guardadas.');
      onClose();
    } catch {
      message.error('No se pudo guardar la configuración de columnas.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width={720}
      title="Editar Columnas del Excel"
      footer={[
        <Button key="cancel" onClick={onClose}>Cancelar</Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleGuardar}>Guardar</Button>,
      ]}
    >
      <Tabs
        activeKey={tab}
        onChange={key => setTab(key as 'factura' | 'pago' | 'nomina')}
        items={[
          { key: 'factura', label: 'Emitidas y Recibidas Facturas' },
          { key: 'pago', label: 'Emitidas y Recibidas Pago' },
          { key: 'nomina', label: 'Nómina' },
        ]}
      />
      <Table<FilaCampo>
        rowKey="id"
        loading={loading}
        dataSource={filas}
        pagination={false}
        size="small"
        scroll={{ y: 400 }}
        columns={[
          { title: 'Orden', dataIndex: 'orden', width: 70, align: 'center' as const },
          { title: 'Columna', dataIndex: 'label' },
          {
            title: 'Visible',
            width: 90,
            align: 'center' as const,
            render: (_: unknown, row: FilaCampo) => (
              <Switch
                checked={!!seleccion[row.id]}
                onChange={checked => setSeleccion(prev => ({ ...prev, [row.id]: checked }))}
              />
            ),
          },
        ]}
      />
    </Modal>
  );
}
